import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  AssessmentClient,
  UpsertAssessmentApplicantDto,
} from '../types/applicant.type';
import type {
  CalculationCreateVersionResponse,
  CalculationData,
  CalculationRestoreResponse,
  CalculationResponse,
  CalculationVersionType,
  CalculationVersionMetadata,
  CalculationVersionPreview,
} from '../types/assessment-calculation.type';
import type { RagGroupResponse } from '@/pages/mortgage-workflow/routes/Documents/types/document.type';
import type { WorksheetLender } from '../types/lender-income.type';
import type { CalculationInputs } from '../types/calculation-inputs.type';

/** Backend returns the post-swap applicants keyed by role, not as an array. */
/**
 * The page's document source stays `pending` (document sections held back)
 * until the portal answers. A slow answer becomes an error instead, so the
 * page falls back to Drive documents with the portal-error strip.
 */
export const PORTAL_INPUTS_TIMEOUT_MS = 15_000;

interface SwapApplicantsResult {
  primary: AssessmentClient;
  coApplicant: AssessmentClient;
}

interface WorksheetLenderPolicyResponse {
  isCurrent: boolean;
  baseSalaryShade: Record<string, number | string> | null;
  bonusSalaryShade: Record<string, number | string> | null;
}

interface WorksheetLenderResponse {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
  includeInLenderIncomeComparison: boolean;
  logoUrl: string | null;
  policies: WorksheetLenderPolicyResponse[];
}

/**
 * Strip null/undefined holes out of any array slice of a calculation upsert.
 * React Hook Form field arrays can end up sparse (e.g. `setValue('exchangeRates.2',
 * …)` with no 0/1) which JSON-serialises to `null` holes; on reload those crash
 * every consumer that maps over the slice and reads its fields. Sanitising in the
 * mutation covers all callers (autosave via saveSection and the direct
 * upsertCalculation calls) at the single point where the request body is built.
 */
const sanitizeCalculationBody = (
  data: Partial<CalculationData> | Record<string, unknown>,
): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    cleaned[key] = Array.isArray(value)
      ? value.filter((item) => item != null)
      : value;
  }
  return cleaned;
};

const toShadeRecord = (
  rec: Record<string, number | string> | null | undefined,
  fallback: Record<string, number>,
): Record<string, number> => {
  if (!rec) return fallback;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(rec)) {
    const n = Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return Object.keys(out).length ? out : fallback;
};

export const creditAnalystCalculationAPI = createApi({
  reducerPath: 'creditAnalystCalculationAPI',
  baseQuery: customBaseQuery,
  tagTypes: [
    'CalculationData',
    'CalculationVersions',
    'ExchangeRates',
    'AssessmentApplicants',
  ],
  endpoints: (builder) => ({
    getCalculationDataByAssessmentId: builder.query<
      CalculationResponse,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => `/calculations/${assessmentId}`,
      transformResponse: (res: ApiResponse<CalculationResponse>) =>
        res?.data ?? {
          sectionsSaved: [],
          applicants: [],
          version: 0,
          latestVersionId: '',
          hasChangesSinceLastVersion: false,
        },
      providesTags: ['CalculationData'],
    }),
    upsertCalculationByAssessmentId: builder.mutation<
      ApiResponse<CalculationResponse>,
      {
        assessmentId: string;
        data: Partial<CalculationData> | Record<string, unknown>;
      }
    >({
      query: ({ assessmentId, data }) => ({
        url: `/calculations/${assessmentId}`,
        method: 'POST',
        body: sanitizeCalculationBody(data),
      }),
      invalidatesTags: ['CalculationData'],
    }),
    createCalculationVersion: builder.mutation<
      CalculationCreateVersionResponse,
      {
        assessmentId: string;
        type?: Extract<
          CalculationVersionType,
          'automatic' | 'manual' | 'submitted'
        >;
      }
    >({
      query: ({ assessmentId, type = 'automatic' }) => ({
        url: `/calculations/${assessmentId}/versions`,
        method: 'POST',
        body: { type },
      }),
      transformResponse: (res: ApiResponse<CalculationCreateVersionResponse>) =>
        res.data,
      invalidatesTags: ['CalculationData', 'CalculationVersions'],
    }),
    getCalculationVersions: builder.query<
      CalculationVersionMetadata[],
      { assessmentId: string; limit?: number; cursor?: number }
    >({
      query: ({ assessmentId, limit = 20, cursor }) => ({
        url: `/calculations/${assessmentId}/versions`,
        params: { limit, ...(cursor ? { cursor } : {}) },
      }),
      transformResponse: (res: ApiResponse<CalculationVersionMetadata[]>) =>
        res?.data ?? [],
      providesTags: ['CalculationVersions'],
    }),
    getCalculationVersion: builder.query<
      CalculationVersionPreview,
      { assessmentId: string; version: number }
    >({
      query: ({ assessmentId, version }) =>
        `/calculations/${assessmentId}/versions/${version}`,
      transformResponse: (res: ApiResponse<CalculationVersionPreview>) =>
        res.data,
    }),
    restoreCalculationVersion: builder.mutation<
      CalculationRestoreResponse,
      { assessmentId: string; version: number }
    >({
      query: ({ assessmentId, version }) => ({
        url: `/calculations/${assessmentId}/versions/${version}/restore`,
        method: 'POST',
      }),
      transformResponse: (res: ApiResponse<CalculationRestoreResponse>) =>
        res?.data ?? {},
      invalidatesTags: ['CalculationData', 'CalculationVersions'],
    }),
    getExchangeRates: builder.query<
      ApiResponse<{
        applicant?: ExchangeRatePayload;
        coapplicant?: ExchangeRatePayload;
      }>,
      { calculationId: string; assessmentType: string }
    >({
      query: ({ calculationId, assessmentType }) =>
        `/calculations/${calculationId}/${assessmentType}/exchange-rates`,
      providesTags: ['ExchangeRates'],
    }),
    upsertExchangeRates: builder.mutation<
      ApiResponse<any>,
      {
        calculationId: string;
        assessmentType: string;
        applicant: ExchangeRatePayload;
        coapplicant?: ExchangeRatePayload;
      }
    >({
      query: ({ calculationId, assessmentType, applicant, coapplicant }) => ({
        url: `/calculations/${calculationId}/${assessmentType}/exchange-rates`,
        method: 'POST',
        body: { applicant, ...(coapplicant ? { coapplicant } : {}) },
      }),
      invalidatesTags: ['ExchangeRates'],
    }),
    getAssessmentApplicants: builder.query<
      ApiResponse<AssessmentClient[]>,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => `/assessments/applicants/${assessmentId}`,
      providesTags: ['AssessmentApplicants'],
    }),
    getAssessmentWorksheetLenders: builder.query<WorksheetLender[], void>({
      query: () =>
        'v1/public/lender-management/lenders?assessmentWorksheetEnabled=true',
      transformResponse: (
        raw: ApiResponse<WorksheetLenderResponse[]> | WorksheetLenderResponse[],
      ): WorksheetLender[] => {
        const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
        return list
          .filter((l) => l.isActive && l.includeInLenderIncomeComparison)
          .map((l) => {
            const policy =
              l.policies?.find((p) => p.isCurrent) ?? l.policies?.[0];
            return {
              id: l.id,
              name: l.name,
              displayName: l.displayName,
              logoUrl: l.logoUrl ?? null,
              baseSalaryShade: toShadeRecord(policy?.baseSalaryShade, {
                _: 1,
                AUD: 1,
              }),
              bonusSalaryShade: toShadeRecord(policy?.bonusSalaryShade, {
                _: 1,
                AUD: 1,
              }),
            };
          });
      },
    }),
    upsertAssessmentApplicant: builder.mutation<
      ApiResponse<AssessmentClient>,
      UpsertAssessmentApplicantDto
    >({
      query: (body) => ({
        url: '/assessments/applicants',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AssessmentApplicants'],
    }),
    deleteAssessmentApplicant: builder.mutation<
      void,
      { applicantId: string; deleteWorksheetData?: boolean }
    >({
      query: ({ applicantId, deleteWorksheetData = false }) => ({
        url: `/assessments/applicants/${applicantId}`,
        method: 'DELETE',
        // Backend leaves the worksheet untouched unless this is true; only send
        // the flag when set so the default request stays a plain DELETE.
        ...(deleteWorksheetData
          ? { params: { deleteWorksheetData: true } }
          : {}),
      }),
      invalidatesTags: ['AssessmentApplicants'],
    }),
    swapAssessmentApplicants: builder.mutation<
      ApiResponse<SwapApplicantsResult>,
      { assessmentId: string }
    >({
      query: ({ assessmentId }) => ({
        url: `/assessments/applicants/swap/${assessmentId}`,
        method: 'POST',
      }),
      invalidatesTags: ['AssessmentApplicants'],
    }),
    getRagResponseFromAssessment: builder.query<ApiResponse<any>, string>({
      query: (assessmentId) =>
        `/v1/crm-orchestration/get-all-rag-responses/assessment/${assessmentId}`,
    }),
    getRagGroupResponseFromAssessment: builder.query<
      ApiResponse<RagGroupResponse[]>,
      string
    >({
      query: (assessmentId) =>
        `/v1/crm-orchestration/assessments/${assessmentId}/rag-responses/by-date`,
    }),
    /** Portal document suggestions (document-classification pipeline). */
    getClassificationCalculationInputs: builder.query<
      CalculationInputs | null,
      string
    >({
      query: (assessmentId) => ({
        url: `/v1/document-classification/mortgage/assessments/${assessmentId}/calculation-inputs`,
        timeout: PORTAL_INPUTS_TIMEOUT_MS,
      }),
      transformResponse: (res: ApiResponse<CalculationInputs>) =>
        res?.data ?? null,
    }),
    createAssessmentProperties: builder.mutation<
      ApiResponse<{
        assessmentId: string;
        clientId: string;
        properties: {
          id: string;
          sourcePropertyId: string;
          propertyLocation: string;
          propertyDetails: Record<string, unknown>;
        }[];
      }>,
      {
        assessmentId: string;
        /** Legacy fallback only; explicit propertyOwnership is authoritative. */
        email?: string;
        /**
         * New properties only, so no `sourcePropertyId`: an existing
         * property is linked with `linkAssessmentProperty` (OMT-1712).
         */
        properties: {
          propertyLocation: string;
          propertyDetails: Record<string, unknown>;
          propertyOwnership?: {
            clientId: string;
            ownershipPercent: number;
          }[];
        }[];
      }
    >({
      query: (body) => ({
        url: '/assessments/properties',
        method: 'POST',
        body,
      }),
    }),
    /**
     * Links an existing property to the assessment (204, idempotent). Leaves
     * the property's details and ownerships untouched.
     */
    linkAssessmentProperty: builder.mutation<
      void,
      { assessmentId: string; propertyId: string }
    >({
      query: ({ assessmentId, propertyId }) => ({
        url: `/assessments/${assessmentId}/properties/${propertyId}`,
        method: 'POST',
      }),
    }),
  }),
});

export interface ExchangeRatePayload {
  email: string;
  from: string;
  to: string;
  rate: number;
  setAt: string;
}

export const {
  useGetCalculationDataByAssessmentIdQuery,
  useUpsertCalculationByAssessmentIdMutation,
  useCreateCalculationVersionMutation,
  useGetCalculationVersionsQuery,
  useLazyGetCalculationVersionsQuery,
  useLazyGetCalculationVersionQuery,
  useRestoreCalculationVersionMutation,
  useGetExchangeRatesQuery,
  useUpsertExchangeRatesMutation,
  useGetRagResponseFromAssessmentQuery,
  useGetRagGroupResponseFromAssessmentQuery,
  useGetClassificationCalculationInputsQuery,
  useCreateAssessmentPropertiesMutation,
  useLinkAssessmentPropertyMutation,
  useGetAssessmentApplicantsQuery,
  useGetAssessmentWorksheetLendersQuery,
  useUpsertAssessmentApplicantMutation,
  useDeleteAssessmentApplicantMutation,
  useSwapAssessmentApplicantsMutation,
} = creditAnalystCalculationAPI;
